import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';

import { appConfig } from '../../config/app';
import { CustomEvents } from '../../constants/analytics.constant';
import { getFormattedDate } from '../../helpers/getDate';
import useLogEventInAnalytics from '../../hooks/useLogAnalyticsEvent';
import { GetPreference } from '../../services/payments/payments.service';
import {
  createEndorsementAPI,
  downloadPolicy,
  policy,
  policyVt7Payment,
  printDocument,
} from '../../services/policy/policy.service';
import {
  searchVehicles,
  vehicle,
  vehiclesAPI,
} from '../../services/vehicles/vehicles.service';
import VehiclesReducer, {
  CLEAN_SUCCESS_ERRORS,
  CLEAR_VEHICLES_SEARCHED,
  FAILURE_ENDORSEMENT,
  FAILURE_POLICY,
  FAILURE_PREFERENCE_ID,
  FAILURE_PRINTABLE_DOCUMENT,
  FAILURE_SEARCH_VEHICLES,
  FAILURE_VEHICLES,
  FAILURE_VEHICLE_BY_ID,
  REQUEST_ENDORSEMENT,
  REQUEST_POLICY,
  REQUEST_PREFERENCE_ID,
  REQUEST_PRINTABLE_DOCUMENT,
  REQUEST_SEARCH_VEHICLES,
  REQUEST_VEHICLES,
  REQUEST_VEHICLE_BY_ID,
  SUCCESS_ENDORSEMENT,
  SUCCESS_POLICY,
  SUCCESS_PRINTABLE_DOCUMENT,
  SUCCESS_SEARCH_VEHICLES,
  SUCCESS_VEHICLES,
  SUCCESS_VEHICLE_BY_ID,
} from './vehicles.reducer';

export const VehiclesContext = createContext<any>(null);

const initialVehicles = {
  errors: {},
  success: {},
  vehicles: [],
  vehiclesSearched: [],
  vehicle: {},
  policies: [],
  userEnabled: true,
  policy: {},
  loading: false,
  loadingPool: [],
};

const VehiclesProvider = ({ ...props }) => {
  const [state, dispatch] = useReducer(VehiclesReducer, initialVehicles);
  const { logEvent } = useLogEventInAnalytics();

  const getVehicles = async () => {
    dispatch({
      type: REQUEST_VEHICLES,
    });
    try {
      const res = await vehiclesAPI();
      dispatch({
        type: SUCCESS_VEHICLES,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_VEHICLES,
      });
    }
  };

  const searchVehiclesByQ = async (q: string, isFiltering?: boolean) => {
    dispatch({
      type: REQUEST_SEARCH_VEHICLES,
    });

    if (isFiltering) {
      dispatch({
        type: CLEAR_VEHICLES_SEARCHED,
      });
    }
    try {
      const res = await searchVehicles(q);
      dispatch({
        type: SUCCESS_SEARCH_VEHICLES,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_SEARCH_VEHICLES,
      });
    }
  };

  const getVehicleById = async (id: number) => {
    dispatch({
      type: REQUEST_VEHICLE_BY_ID,
    });
    try {
      const res = await vehicle(id);
      dispatch({
        type: SUCCESS_VEHICLE_BY_ID,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_VEHICLE_BY_ID,
      });
    }
  };

  const getPolicyById = useCallback(async (id: number) => {
    dispatch({ type: REQUEST_POLICY });
    try {
      const res = await policy(id);
      if (res.data?.sura_provider_id === 1) {
        const resVt7 = await policyVt7Payment({
          branch: res.data.gs_branch,
          product: res.data.gs_product,
          policy: res.data.number,
        });
        const resVt7Data = resVt7.data.data[0]?.certificates[0];
        if (resVt7Data) {
          const pending =
            resVt7Data.receipts
              .filter((r) => r.due !== 0)
              .map((r) => ({
                id: r.nReceipt,
                expiration_date: getFormattedDate(
                  new Date(r.dLimitDate).toLocaleDateString()
                ),
                amount: r.due.toFixed(2),
              }))[0] || null;
          res.data.financial = {
            ...res.data.financial,
            error: false,
            maturities: {
              id: 0,
              days: 0,
              amount: resVt7Data.totalDebt.toFixed(2),
            },
            pending,
          };
        }
      }

      dispatch({ type: SUCCESS_POLICY, payload: res.data });
    } catch (err) {
      dispatch({ type: FAILURE_POLICY });
    }
  }, []);

  const getPrintableDocument = async (
    id: any,
    doc_type: any,
    doc_name: any,
    //endorsement_id: any,
    alternative_email?: any
  ) => {
    dispatch({
      type: REQUEST_PRINTABLE_DOCUMENT,
    });
    try {
      const res = await printDocument(
        id,
        doc_type,
        //endorsement_id,
        alternative_email
      );
      await logEvent(CustomEvents.DOCUMENTATION_SEND, {
        id: doc_type,
        name: doc_name,
        app: appConfig?.NAME,
      });
      dispatch({
        type: SUCCESS_PRINTABLE_DOCUMENT,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_PRINTABLE_DOCUMENT,
      });
    }
  };

  const getDownloadPolicy = async (id) => {
    try {
      const result = await downloadPolicy(id);
      return result;
    } catch (error) {
      return { error: 'error at download policy' };
    }
  };

  const cleanSuccessErrors = () => {
    dispatch({
      type: CLEAN_SUCCESS_ERRORS,
    });
  };

  const cleanVehicles = () => {
    dispatch({
      type: CLEAR_VEHICLES_SEARCHED,
    });
  };

  const getPreferenceId = async (body: object) => {
    try {
      const res = await GetPreference(body);
      dispatch({
        type: REQUEST_PREFERENCE_ID,
        payload: res.data.preference_id,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_PREFERENCE_ID,
        payload: err,
      });
    }
  };

  const createEndorsement = async (data: any) => {
    dispatch({
      type: REQUEST_ENDORSEMENT,
    });

    try {
      const res = await createEndorsementAPI(state.policy.id, data);
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          if (key !== 'vehicle_id') {
            await logEvent(CustomEvents.ENDORSEMENTS_BASE + key, {
              app: appConfig?.NAME,
            });
          }
        }
      }
      dispatch({
        type: SUCCESS_ENDORSEMENT,
        payload: res.data,
      });
    } catch (err) {
      const data = {
        ...err.response?.data,
        status: err.response.status,
      };
      dispatch({
        type: FAILURE_ENDORSEMENT,
        payload: data,
      });
    }
  };

  const VehiclesValue = useMemo(
    () => ({
      ...state,
      getVehicles,
      getVehicleById,
      getPolicyById,
      getPreferenceId,
      searchVehiclesByQ,
      getPrintableDocument,
      getDownloadPolicy,
      cleanSuccessErrors,
      createEndorsement,
      cleanVehicles,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <VehiclesContext.Provider value={VehiclesValue} {...props} />;
};

export const useVehiclesContext = () => useContext(VehiclesContext);

export default VehiclesProvider;
